import SpinnerFullScreen from '../../../../components/dependencies/Spinner';
import React, { EffectCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { angularService, angularEmit } from '../../../../utils/angularBridge';

import { getCustomComponents } from '../api';

import { FormSearchInput } from './FormElements';
import styled from 'styled-components';

export type Component = {
    Labels: { Name: string }[];
};

export type CustomComponentsListProps = {
    onLayoutChange?: EffectCallback;
};

export const CustomComponentsList = ({ onLayoutChange }: CustomComponentsListProps) => {
    const { isLoading, data } = useQuery({
        queryKey: ['wf-custom-components'],
        queryFn: getCustomComponents,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (data) {
            angularEmit('react:editor-update-signal-list', data.data);
        }
    }, [data]);

    const [filters, setFilter] = useState({ searchText: '', labels: ['All'] });
    const isOnlyTaskComponents = (component: { Type: string; SubType: string }) =>
        component.Type === 'Task' && ['Workflow', 'RemoteFunction', 'LocalFunction'].indexOf(component.SubType) > -1;
    // eslint-disable-next-line
    const components: Component[] = data ? data.data.filter(isOnlyTaskComponents) : [];
    const labels = useMemo(
        () =>
            components.reduce((acc, component) => {
                const labels = component.Labels || [];
                labels.forEach((label) => {
                    if (acc.indexOf(label.Name) === -1) {
                        acc.push(label.Name);
                    }
                });
                return acc;
            }, [] as any[]),
        [components],
    );
    const setSearchTextFilterFn = (searchText: string) => setFilter((prevState) => ({ ...prevState, searchText }));
    const setLabelFilterFn = (labels: any) => setFilter((prevState) => ({ ...prevState, labels }));
    const applyFiltersFn = (filters: { searchText: any; labels: any }, components: any[]) => {
        const isEmptyFilter =
            !filters.searchText.length &&
            (!filters.labels.length || (filters.labels[0] && filters.labels[0] === 'All'));
        const isSearchByText =
            !!filters.searchText.length &&
            (!filters.labels.length || (filters.labels[0] && filters.labels[0] === 'All'));
        const isSearchByLabels =
            !filters.searchText.length && filters.labels.length && filters.labels[0] && filters.labels[0] !== 'All';
        const isSearchByTextAndLabels =
            !!filters.searchText.length && filters.labels.length && filters.labels[0] && filters.labels[0] !== 'All';

        const hasSearchTextFn = (component: { DisplayName: string }) =>
            filters.searchText.length
                ? component.DisplayName.toLowerCase().indexOf(filters.searchText.toLowerCase()) > -1
                : false;

        const hasLabelsFn = (component: { Labels: any[] }) =>
            component.Labels ? component.Labels.some((label) => filters.labels.indexOf(label.Name) > -1) : false;

        if (isEmptyFilter) {
            return components;
        }

        if (isSearchByTextAndLabels) {
            return components.filter((component) => {
                const hasSearchText = hasSearchTextFn(component);
                const hasLabels = hasLabelsFn(component);

                return hasSearchText && hasLabels;
            });
        }

        if (isSearchByText) {
            return components.filter(hasSearchTextFn);
        }

        if (isSearchByLabels) {
            return components.filter(hasLabelsFn);
        }

        return components;
    };
    const componentsFiltered = applyFiltersFn(filters, components).sort((a, b) => a.Name.localeCompare(b.Name));

    useEffect(() => {
        if (onLayoutChange) {
            onLayoutChange();
        }
        // eslint-disable-next-line
    }, [components && components.length, isLoading]);

    if (!components.length && !isLoading) {
        return (
            <div
                style={{
                    color: '#8C8C8C',
                    fontSize: '12px',
                    fontStyle: 'italic',
                    lineHeight: '20px',
                    marginTop: '8px',
                }}
            >
                No custom components created yet
            </div>
        );
    }

    return (
        <Wrapper>
            <Title>Component library</Title>

            <FormSearchInput
                id={'search'}
                name="search"
                type="text"
                value={filters.searchText}
                label={''}
                onChange={(e) => {
                    setSearchTextFilterFn(e.value);
                }}
            />

            <LabelsFilter
                list={labels}
                values={filters.labels}
                itemCount={componentsFiltered.length}
                onChange={setLabelFilterFn}
            />

            {isLoading && <SpinnerFullScreen />}

            <div style={{ flex: '1 1 0%', overflow: 'auto' }} className="scrollbar">
                {componentsFiltered.map((cc) => {
                    const labels = cc.Labels ? cc.Labels.map((label: { Name: any }) => label.Name) : [];
                    return (
                        <CustomComponent
                            key={cc.Name}
                            name={cc.Name}
                            displayName={cc.DisplayName}
                            description={cc.Description}
                            labels={labels}
                            icon={cc.Icon}
                            workflowName={cc.WorkflowName}
                            workflowVersion={cc.WorkflowVersion}
                            type={cc.Type}
                            subType={cc.SubType}
                            schema={cc.Schema}
                            onLabelSelect={setLabelFilterFn}
                        />
                    );
                })}
            </div>
        </Wrapper>
    );
};

/**
 * LabelsFilter component
 */

const LabelsFilterStyle = styled.div`
    &&& {
        margin-top: 24px;
        margin-bottom: 22px;
        flex-direction: column;

        .tagTitleWrapper {
            display: flex;
            margin: 0;
        }

        .tagTitle {
            color: #3197c8;
            font-weight: bold;
            font-size: 10px;
            cursor: pointer;

            &:hover {
                text-decoration: underline;
            }
        }

        .tagCount {
            color: #3197c8;
            font-size: 10px;
            padding-left: 5px;
        }

        .labels {
            display: flex;
            flex: 1 1 1px;
            flex-wrap: wrap;
            margin-top: 5px;
        }

        .label {
            font-size: 10px;
            margin-right: 7px;
            border: 1px solid #dadce5;
            border-radius: 4px;
            display: flex;
            justify-items: center;
            align-items: center;
            cursor: pointer;
            color: #7c86a3;
            margin-bottom: 5px;
            padding: 2px 8px;
            font-weight: 700;

            :hover {
                color: #697595;
                background: #f0f2f5;
                border: 1px solid #f0f2f5;
            }
        }

        .labelActive {
            color: #697595;
            background: #f0f2f5;
            border: 1px solid #f0f2f5;
        }
    }
`;

const LabelsFilter = ({ list, onChange, values, itemCount }: any) => {
    const [activeLabels, setActiveLabel] = useState(values);

    useEffect(() => {
        setActiveLabel(values);
    }, [values]);

    const toggleLabelFn = (labelName: string) => {
        const inList = activeLabels.includes(labelName);
        const callbackFn = (activeLabels: any) => {
            if (onChange) {
                onChange(activeLabels);
            }
        };

        switch (true) {
            case labelName === 'All': {
                setActiveLabel(['All']);
                callbackFn(['All']);
                return;
            }
            case inList: {
                const labels = activeLabels.filter((label: string) => label !== labelName);
                const labelsToAdd = labels.length === 0 ? ['All'] : labels;
                setActiveLabel(labelsToAdd);
                callbackFn(labelsToAdd);
                return;
            }
            default: {
                const labels: string[] = activeLabels.filter((label: string) => label !== 'All');
                setActiveLabel([...labels, labelName]);
                callbackFn([...labels, labelName]);
            }
        }
    };

    return (
        <LabelsFilterStyle>
            <div className="tagTitleWrapper">
                <div className="tagTitle" onClick={() => toggleLabelFn('All')}>
                    Show all
                </div>
                <div className="tagCount">{itemCount}</div>
            </div>
            <div className="labels">
                {list.map((label: any) => (
                    <div
                        key={label}
                        className={`label ${activeLabels.includes(label) ? 'labelActive' : ''}`}
                        onClick={() => toggleLabelFn(label)}
                    >
                        {label}
                    </div>
                ))}
            </div>
        </LabelsFilterStyle>
    );
};

/**
 * Wrapper component
 */
const Wrapper = styled.div`
    &&& {
        padding: 24px;
        overflow: hidden;
        flex-grow: 1;
        display: flex;
        flex-direction: column;
    }
`;

/**
 * ItemWrapper component
 */
const CustomComponentStyle = styled.div`
    &&& {
        display: flex;
        align-items: flex-start;
        margin-bottom: 10px;
        border: 1px solid #eceef2;
        border-radius: 3px;
        padding: 10px 10px 10px 20px;
        cursor: pointer;

        &:last-child {
            margin-bottom: 0;
        }

        .component-name {
            word-break: break-all;
        }

        &:hover {
            background: #f0f2f5;

            .component-name {
                color: #697595;
                cursor: pointer;
            }
        }
    }
`;
const CustomComponent = ({
    displayName,
    name,
    description,
    labels,
    icon,
    onLabelSelect,
    workflowName,
    workflowVersion,
    type,
    subType,
    schema,
}: any) => {
    const [isHovered, setHover] = useState(false);
    const [uniqId] = useState(() => '_' + Math.random().toString(36).substr(2, 9));

    const handleDragStart = (e: any) => {
        e.dataTransfer.setData('text', uniqId);
        // @ts-ignore
        e.dataTransfer.setData('offsetX', e.nativeEvent.offsetX);
        // @ts-ignore
        e.dataTransfer.setData('offsetY', e.nativeEvent.offsetY);
        e.dataTransfer.setData('name', name);
        e.dataTransfer.setData('displayName', displayName);
        e.dataTransfer.setData('description', description);
        e.dataTransfer.setData('workflowName', workflowName);
        e.dataTransfer.setData('workflowVersion', workflowVersion);

        window.dragDropUtils.setDragEventPreviewImage(e);
    };

    return (
        <>
            <CustomComponentStyle
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                data-shape-type="ComponentViewModel"
                data-ng-draggable="True"
                draggable={true}
                onDragStart={handleDragStart}
                id={uniqId}
            >
                <CustomIconItem icon={!icon || icon === 'default' ? 'fas fa-cube' : icon} isHovered={isHovered} />
                <div style={{ position: 'relative' }}>
                    <Name className="component-name">{displayName}</Name>

                    {description && (
                        <div style={{ fontSize: '10px', color: '#898989', lineHeight: '20px' }}>{description}</div>
                    )}

                    <div style={{ position: 'relative', top: '2px', width: '200px' }}>
                        <Labels list={labels} onLabelSelect={onLabelSelect} />
                    </div>
                </div>

                <DragPreview icon={icon} displayName={displayName} />
            </CustomComponentStyle>
        </>
    );
};

/**
 * Name component
 */

const Name = styled.div`
    &&& {
        font-size: 12px;
        color: #595959;
        font-weight: 700;
    }
`;

/**
 * Labels Component
 */
const LabelsStyle = styled.span<any>`
    &&& {
        font-size: 10px;
        cursor: pointer;
        color: #333333;

        &:hover {
            text-decoration: underline;
        }
    }
`;
const Labels = ({ list, onLabelSelect }: any) => {
    return (
        <>
            {list.map((label: React.Key, i: number) => {
                const shouldAddComma = list.length - 1 !== i;
                return (
                    <React.Fragment key={label}>
                        <LabelsStyle key={label} onClick={() => onLabelSelect([label])}>
                            {label}
                        </LabelsStyle>
                        {shouldAddComma && <span>, </span>}
                    </React.Fragment>
                );
            })}
        </>
    );
};

/**
 * Custom icon component
 */
type CustomIconItemStyleProps = { isHovered: boolean };
const CustomIconItemStyle = styled.div<CustomIconItemStyleProps>`
    &&& {
        width: 40px;
        height: 40px;
        border-radius: 8px;
        border-color: ${(props: CustomIconItemStyleProps) => (!props.isHovered ? '#D9D9D9' : '#697595')};

        cursor: pointer;

        text-align: center;
        line-height: 31px;

        margin-right: 8px;

        span {
            font-size: 16px;
            color: ${(props: CustomIconItemStyleProps) => (!props.isHovered ? '#807f7f' : '#697595')};
        }
    }
`;

const CustomIconItem = ({ icon, isHovered, onSelect }: any) => {
    return (
        <CustomIconItemStyle isHovered={isHovered} onClick={onSelect}>
            <span className={icon} />
        </CustomIconItemStyle>
    );
};

/**
 * Title component
 */

const Title = styled.div`
    &&& {
        font-size: 12px;
        font-weight: 500;
        color: #555555;
    }
`;

const DragPreviewStyles = styled.div`
    &&& {
        display: block;
        line-height: 0;
        min-width: unset;

        rect {
            -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
            fill-opacity: 0.9;
            cursor: pointer;
            fill: #fafafa;
            stroke: #555555;
            stroke-width: 1;
        }

        .title {
            -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
            text-anchor: middle;
            font: 13px consolas;
            cursor: move;
            stroke: none;
            fill: #333333;
            stroke-width: 1;
        }

        image {
            -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
            cursor: pointer;
            stroke-width: 1;
        }

        .previewIcon {
            -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
            font-family: 'font awesome 5 free';
            // fontsize: 14px;
            font-size: 10px;
            cursor: pointer;
            stroke: none;
            stroke-width: 1;
            fill: #5b5b5b;

            tspan {
                -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
            }
        }
    }
`;

const DragPreview = ({ icon, displayName }: { icon: string; displayName: string }) => {
    const iconUnicode = angularService('fontAwesomeService').getUnicode(icon);

    return (
        <DragPreviewStyles data-ng-draggable-preview-hidden="True">
            <svg width="33" height="21" viewBox="0 0 33 21" fill="white" xmlns="http://www.w3.org/2000/svg">
                <g data-ng-draggable-preview="True">
                    <path
                        d="M1.14834 4C1.14834 2.12223 2.67057 0.6 4.54834 0.6H28.5483C30.4261 0.6 31.9483 2.12223 31.9483 4V17.3333C31.9483 19.2111 30.4261 20.7333 28.5483 20.7333H4.54834C2.67057 20.7333 1.14834 19.2111 1.14834 17.3333V4Z"
                        stroke="#555555"
                        strokeWidth="1.2"
                    />

                    <text x="16.5" y="15" textAnchor="middle">
                        <tspan className="previewIcon">{iconUnicode}</tspan>
                    </text>
                </g>
            </svg>
        </DragPreviewStyles>
    );
};
